/**
 * 路由
 *
 * Web 端：默认相对路径部署（base='./'，产物放任意目录都能跑）→ 走 hash 模式；
 *         配了 VITE_PUBLISH_PATH=/web 这类虚拟目录时 → 走 history 模式
 * 桌面端：hash 模式（file:// 加载，history 模式会失效）
 *
 * meta 说明：
 *  - title    侧边栏 / 顶栏标题
 *  - icon     侧边栏图标名（AppIcon）
 *  - group    侧边栏分组
 *  - auth     是否需要登录
 *  - hide     是否不在侧边栏展示
 */
import { createRouter, createWebHistory, createWebHashHistory, type RouteRecordRaw } from 'vue-router'
import { AI_IDENTITY_NAME } from '@/config/global'

/** 是否桌面端（编译期常量，Web 构建里会被 DCE 掉 hash 分支） */
const isElectron = __ELECTRON__

/** 路由表 */
export const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/login/index.vue'),
    meta: { title: '登录', hide: true, auth: false, fullscreen: true }
  },
  {
    path: '/',
    component: () => import('@/layout/DesktopLayout.vue'),
    redirect: '/dashboard',
    children: [
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/index.vue'),
        meta: { title: '首页', icon: 'home', group: 'main', auth: true }
      },
      {
        path: 'create',
        name: 'create',
        component: () => import('@/views/create/index.vue'),
        meta: { title: '创作内容', icon: 'create', group: 'main', auth: true }
      },
      {
        path: 'create/video',
        name: 'create-video',
        component: () => import('@/views/create/video.vue'),
        meta: { title: '视频创作', auth: true, hide: true }
      },
      {
        path: 'create/article',
        name: 'create-article',
        component: () => import('@/views/create/article.vue'),
        meta: { title: '图文创作', auth: true, hide: true }
      },
      {
        path: 'create/expert/:id',
        name: 'create-expert',
        component: () => import('@/views/create/expert.vue'),
        meta: { title: '专属创作', auth: true, hide: true }
      },
      /**
       * AI 助手：入口改为右下角常驻悬浮按钮（components/Common/AiAssistantFab.vue），
       * 点击从右侧弹出聊天窗口，所以不在侧边栏展示；路由保留，深链 /ai 仍可直达。
       */
      {
        path: 'ai',
        name: 'ai',
        component: () => import('@/views/ai/index.vue'),
        meta: { title: 'AI 助手', icon: 'ai', auth: true, hide: true }
      },
      {
        path: 'content',
        name: 'content',
        component: () => import('@/views/content/index.vue'),
        meta: { title: '我的内容', icon: 'content', group: 'main', auth: true }
      },
      {
        path: 'content/:id',
        name: 'content-detail',
        component: () => import('@/views/content/detail.vue'),
        meta: { title: '内容详情', auth: true, hide: true }
      },
      {
        path: 'publish',
        name: 'publish',
        component: () => import('@/views/publish/index.vue'),
        meta: { title: '发布内容', icon: 'publish', group: 'publish', auth: true }
      },
      {
        path: 'publish/records',
        name: 'publish-records',
        component: () => import('@/views/publish/records.vue'),
        meta: { title: '发布记录', icon: 'content', group: 'publish', auth: true }
      },
      {
        path: 'platform',
        name: 'platform',
        component: () => import('@/views/platform/index.vue'),
        meta: { title: '账号管理', icon: 'platform', group: 'publish', auth: true }
      },
      {
        path: 'platform/guide/:code',
        name: 'platform-guide',
        component: () => import('@/views/platform/guide.vue'),
        meta: { title: '发布指南', auth: true, hide: true }
      },
      {
        path: 'material',
        name: 'material',
        component: () => import('@/views/material/index.vue'),
        meta: { title: '素材库', icon: 'material', group: 'resource', auth: true }
      },
      {
        path: 'material/hot',
        name: 'material-hot',
        component: () => import('@/views/material/hot.vue'),
        meta: { title: '爆文中心', icon: 'content', group: 'resource', auth: true }
      },
      {
        path: 'material/topic',
        name: 'material-topic',
        component: () => import('@/views/material/topic.vue'),
        meta: { title: '话题中心', icon: 'bell', group: 'resource', auth: true }
      },
      {
        path: 'material/copywriting',
        name: 'material-copywriting',
        component: () => import('@/views/material/copywriting.vue'),
        meta: { title: '文案中心', icon: 'edit', group: 'resource', auth: true }
      },
      /** AIIP：右上角切换器里已有入口，侧边栏不再重复展示 */
      {
        path: 'identity',
        name: 'identity',
        component: () => import('@/views/identity/index.vue'),
        meta: { title: AI_IDENTITY_NAME, icon: 'identity', group: 'resource', auth: true, hide: true }
      },
      {
        path: 'identity/create',
        name: 'identity-create',
        component: () => import('@/views/identity/create.vue'),
        meta: { title: `创建${AI_IDENTITY_NAME}`, auth: true, hide: true }
      },
      {
        path: 'identity/:id',
        name: 'identity-detail',
        component: () => import('@/views/identity/detail.vue'),
        meta: { title: `${AI_IDENTITY_NAME}详情`, auth: true, hide: true }
      },
      {
        path: 'template',
        name: 'template',
        component: () => import('@/views/template/index.vue'),
        meta: { title: '模板中心', icon: 'template', group: 'resource', auth: true }
      },
      /**
       * 以下「我的」相关页面：入口统一在左侧边栏底部的用户菜单面板里，
       * 且都是以弹窗形式打开（见 components/Common/UserViewDialog.vue），
       * 所以侧边栏主菜单里全部隐藏，路由本身保留（深链 / 弹窗内跳转仍可用）。
       */
      {
        path: 'points',
        name: 'points',
        component: () => import('@/views/points/index.vue'),
        meta: { title: '积分中心', icon: 'points', auth: true, hide: true }
      },
      {
        path: 'points/records',
        name: 'points-records',
        component: () => import('@/views/points/records.vue'),
        meta: { title: '积分记录', icon: 'content', auth: true, hide: true }
      },
      {
        path: 'invite',
        name: 'invite',
        component: () => import('@/views/invite/index.vue'),
        meta: { title: '邀请好友', icon: 'invite', auth: true, hide: true }
      },
      {
        path: 'stats',
        name: 'stats',
        component: () => import('@/views/stats/index.vue'),
        meta: { title: '数据统计', icon: 'stats', auth: true, hide: true }
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/profile/index.vue'),
        meta: { title: '个人中心', icon: 'user', auth: true, hide: true }
      },
      {
        path: 'settings',
        name: 'settings',
        component: () => import('@/views/settings/index.vue'),
        meta: { title: '系统设置', icon: 'settings', auth: true, hide: true }
      },
      {
        path: 'help',
        name: 'help',
        component: () => import('@/views/help/index.vue'),
        meta: { title: '帮助中心', icon: 'help', auth: false, hide: true }
      },
      {
        path: 'agreement/:type?',
        name: 'agreement',
        component: () => import('@/views/agreement/index.vue'),
        meta: { title: '协议条款', auth: false, hide: true }
      }
    ]
  },
  { path: '/:pathMatch(.*)*', redirect: '/dashboard' }
]

/** 构建期注入的部署前缀：'./' 表示相对路径部署，其余为绝对虚拟目录（如 /web/） */
const deployBase = String(import.meta.env.BASE_URL || '/')

/**
 * 桌面端 file:// 加载必须 hash；
 * Web 端相对路径部署（base='./'）同样必须 hash —— 否则 /material/hot 这类二级路由刷新时，
 * index.html 里的 ./static/js/xxx.js 会按 /material/static/js/xxx.js 去取，直接 404。
 */
const useHashHistory = isElectron || __WEB_HASH__

export const router = createRouter({
  history: useHashHistory ? createWebHashHistory() : createWebHistory(deployBase),
  routes
})

/**
 * 侧边栏分组定义
 *
 * 没有「我的」分组：个人中心 / 积分 / 邀请 / 统计 / 帮助 / 设置 等
 * 统一收进左侧边栏底部的用户菜单面板（弹窗打开）。
 */
export const SIDEBAR_GROUPS: { key: string; label: string }[] = [
  { key: 'main', label: '创作' },
  { key: 'publish', label: '发布' },
  { key: 'resource', label: '资源' }
]

export default router
